/**
 * TicketPreviewPanel
 *
 * Slide-over panel used wherever a quick ticket preview is needed — currently
 * the Tickets list page (HubSpot-only rows) and the Dashboard (HubSpot-only
 * rows in the Recent Tickets widget).
 *
 * Props
 * ─────
 * row      — normalised ticket row (see shape below)
 * onClose  — called when the panel should close
 *
 * Row shape (all optional except ref/subject):
 *   source           'portal' | 'hubspot' | 'both'
 *   portalId         number | null
 *   hubspotId        string | null     — HubSpot ticket object ID
 *   hubspotTicketRef string | null     — e.g. "TKT-00301"
 *   portalTicketNumber string | null   — e.g. "AW-2026-0019"
 *   ref              string            — display reference (derived from above)
 *   subject          string
 *   customerName     string
 *   customerMobile   string
 *   entityName       string
 *   complaintGroup   string
 *   complaintType    string
 *   channelSource    string
 *   priority         string
 *   status           string
 *   assignedToName   string
 *   description      string
 *   createdAt        string (ISO date)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, Mail } from 'lucide-react';
import api from '../utils/api';
import { StatusBadge } from './StatusBadge';

// DD/MM/YYYY HH:mm
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return '—';
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} `
       + `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

const PRIORITY_BADGE = {
  critical: 'bg-red-100 text-red-700 border border-red-200',
  high:     'bg-orange-100 text-orange-700 border border-orange-200',
  medium:   'bg-yellow-100 text-yellow-700 border border-yellow-200',
  low:      'bg-green-100 text-green-700 border border-green-200',
};

const CHANNEL_ICONS = {
  phone: '📞', whatsapp: '💬', email: '📧',
  instagram: '📸', talabat: '🛵', keeta: '🛵', portal: '🖥',
};

function InfoRow({ label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-400 flex-shrink-0 pt-0.5">{label}</span>
      <span className={`text-sm text-gray-800 font-medium text-right break-all ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </span>
    </div>
  );
}

export default function TicketPreviewPanel({ row, onClose }) {
  const navigate = useNavigate();
  const [enrichment, setEnrichment] = useState({ content: '', contactEmail: '', contactName: '' });
  const [enrichLoading, setEnrichLoading] = useState(false);

  // Fetch HubSpot enrichment (original message content + contact email) whenever
  // the panel opens with a HubSpot ticket ID.
  useEffect(() => {
    const hsId = row?.hubspotId;
    if (!hsId) return;
    setEnrichLoading(true);
    setEnrichment({ content: '', contactEmail: '', contactName: '' });
    api.get(`/hubspot/ticket-detail/${hsId}`)
      .then(r => setEnrichment(r.data || {}))
      .catch(() => {})
      .finally(() => setEnrichLoading(false));
  }, [row?.hubspotId]);

  if (!row) return null;

  const ref = row.hubspotTicketRef || row.portalTicketNumber || row.ref || '—';
  const priorityCls = PRIORITY_BADGE[(row.priority || '').toLowerCase()]
    || 'bg-gray-100 text-gray-500 border border-gray-200';
  const channelIcon = CHANNEL_ICONS[(row.channelSource || '').toLowerCase()] || '📋';

  // Resolved customer email: prefer portal-stored email (not in row for HubSpot-only),
  // fall back to enrichment contact email fetched from HubSpot Associations API.
  const displayEmail = enrichment.contactEmail;

  // Show "Original Message" section when HubSpot content differs from the
  // portal description (or when there is no portal description at all).
  const showOriginalMessage =
    enrichment.content &&
    enrichment.content.trim() !== (row.description || '').trim();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/25 z-40 backdrop-blur-[1px]"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col">

        {/* ── Header ── */}
        <div className="bg-[#1a3a5c] px-5 py-5 flex items-start justify-between flex-shrink-0">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-blue-300 uppercase tracking-widest mb-1">
              {row.source === 'hubspot' ? 'HubSpot Ticket'
                : row.source === 'both'   ? 'Portal + HubSpot'
                : 'Portal Ticket'}
            </p>
            <h2 className="text-2xl font-bold text-white truncate">{ref}</h2>
            {row.portalTicketNumber && row.hubspotTicketRef &&
              row.portalTicketNumber !== row.hubspotTicketRef && (
              <p className="text-xs text-blue-300 mt-0.5">{row.portalTicketNumber}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="ml-3 flex-shrink-0 p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Subject bar ── */}
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
          <p className="text-sm text-gray-700 font-medium leading-snug">{row.subject || '—'}</p>
        </div>

        {/* ── Badges ── */}
        <div className="flex gap-2 flex-wrap px-5 py-3 border-b border-gray-100 flex-shrink-0">
          {row.priority && (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${priorityCls}`}>
              {row.priority.charAt(0).toUpperCase() + row.priority.slice(1)} Priority
            </span>
          )}
          {row.status && <StatusBadge status={row.status} />}
          {row.channelSource && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
              {channelIcon} {row.channelSource}
            </span>
          )}
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* Customer */}
          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Customer</h4>
            <div className="bg-gray-50 rounded-xl p-4">
              <InfoRow label="Name"   value={row.customerName} />
              <InfoRow label="Mobile" value={row.customerMobile} />
              {/* Email: loaded from HubSpot contact via enrichment */}
              {enrichLoading && !displayEmail ? (
                <div className="flex items-start justify-between gap-4 py-2">
                  <span className="text-xs text-gray-400 flex-shrink-0 pt-0.5">Email</span>
                  <div className="h-3 w-40 bg-gray-200 rounded animate-pulse" />
                </div>
              ) : displayEmail ? (
                <div className="flex items-start justify-between gap-4 py-2 border-t border-gray-50">
                  <span className="text-xs text-gray-400 flex-shrink-0 pt-0.5">Email</span>
                  <span className="text-sm text-gray-800 font-medium text-right break-all flex items-center gap-1">
                    <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    {displayEmail}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          {/* Complaint Details */}
          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Complaint Details</h4>
            <div className="bg-gray-50 rounded-xl p-4">
              <InfoRow label="Entity"          value={row.entityName} />
              <InfoRow label="Complaint Group" value={row.complaintGroup} />
              <InfoRow label="Complaint Type"  value={row.complaintType} />
              <InfoRow label="Assigned To"     value={row.assignedToName} />
              <InfoRow label="Created"         value={fmtDate(row.createdAt)} />
            </div>
          </div>

          {/* Description (portal-stored) */}
          {row.description && (
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Description</h4>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {row.description}
                </p>
              </div>
            </div>
          )}

          {/* Original Message (raw HubSpot content — shown when different from description) */}
          {enrichLoading && !enrichment.content && (
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                Original Message
              </h4>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-3/4 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-1/2 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          )}
          {showOriginalMessage && (
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                Original Message
              </h4>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="relative pl-3">
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-300 rounded-full" />
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {enrichment.content}
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* ── Footer: navigate to full detail for portal tickets ── */}
        {row.portalId && (
          <div className="border-t border-gray-100 px-5 py-4 flex-shrink-0">
            <button
              onClick={() => navigate(`/tickets/${row.portalId}`)}
              className="flex items-center justify-center gap-2 w-full bg-[#1a3a5c] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-[#15304e] transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Open Full Ticket Detail
            </button>
          </div>
        )}

      </div>
    </>
  );
}
